import { useCallback, useEffect, useRef, useState } from "react";

import { type BotConfig, botConfigsEndpoints } from "../../../api";
import { useStrapiList } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import { hasDataChanged } from "../../../utils/hasDataChanged";
import { getFromCache, setToCache } from "../../../utils/memoryCache";
import handleError from "../../utils/handleError";

interface UseBotsFilters {
  search?: string;
  onLoss?: boolean;
  /** Hours; matches bots whose last stats snapshot is older than this (or missing). */
  staleSnapshotHours?: number;
  disabled?: boolean | Record<string, unknown>;
  user?: unknown;
  [key: string]: unknown;
}

interface StrapiFilter {
  $and?: Record<string, unknown>[];
  stats?: Record<string, unknown>;
  disabled?: boolean | Record<string, unknown>;
  user?: unknown;
  [key: string]: unknown;
}

/**
 * Transform user-friendly filter keys to Strapi filter format
 * Filters stay as structured objects - qs serializer handles conversion to query string
 */
const transformFilters = (filters: UseBotsFilters | undefined): StrapiFilter | undefined => {
  if (!filters) return undefined;

  const result: StrapiFilter = {};

  for (const [key, val] of Object.entries(filters)) {
    if (val === undefined || val === null || val === "") continue;

    switch (key) {
      case "search":
        // Complex OR filter for search across multiple fields
        result.$and = result.$and || [];
        result.$and.push({
          $or: [
            { user: { email: { $containsi: val } } },
            { user: { name: { $containsi: val } } },
            { exchange_account: { name: { $containsi: val } } },
            { exchange_account: { accountId: { $containsi: val } } },
          ],
        });
        break;
      case "onLoss":
        if (val) {
          result.stats = { ...(result.stats || {}), absolute_profit: { $lt: 0 } };
        }
        break;
      case "staleSnapshotHours": {
        // "Stale" covers both an old snapshot and no snapshot at all, which is
        // how the admin overview counts them.
        const hours = Number(val);
        if (!Number.isFinite(hours) || hours <= 0) break;
        const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
        result.$and = result.$and || [];
        result.$and.push({
          $or: [{ stats: { timestamp: { $lt: cutoff } } }, { stats: { timestamp: { $null: true } } }, { stats: { $null: true } }],
        });
        break;
      }
      case "disabled":
        // Support both boolean and object filter (e.g., { $ne: true })
        result.disabled = filters.disabled;
        break;
      case "user":
        result.user = val;
        break;
      default:
        // Pass through other filters as-is
        result[key] = val;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

interface UseBotsProps {
  cacheKey?: string;
  filters?: UseBotsFilters;
  mine?: boolean;
  autoLoad?: boolean;
  [key: string]: unknown;
}

// autoLoad, sort, pagination, filters, populate
const useBots = (props?: UseBotsProps) => {
  const { cacheKey, filters: rawFilters, mine, ...otherProps } = props || {};
  const cached = cacheKey ? getFromCache<BotConfig[]>(cacheKey) : null;
  const [cachedItems, setCachedItems] = useState<BotConfig[]>(cached?.data || []);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasInitialCache = useRef(!!cached);
  const mountedRef = useRef(true);

  // Re-seed when the cache key changes.
  //
  // `cachedItems` was only ever seeded by the initial state, which is
  // evaluated once per mount. Navigating between two pages served by the same
  // route component (admin user A -> user B) changes the key but keeps the
  // component instance, so `items` below kept returning the previous key's
  // rows: opening user B showed user A's bots, and vice versa. Worse,
  // `hasInitialCache` stayed true, so no loading state was shown and the stale
  // rows looked authoritative.
  const seededCacheKeyRef = useRef(cacheKey);
  if (seededCacheKeyRef.current !== cacheKey) {
    seededCacheKeyRef.current = cacheKey;
    const nextCached = cacheKey ? getFromCache<BotConfig[]>(cacheKey) : null;
    setCachedItems(nextCached?.data || []);
    hasInitialCache.current = !!nextCached;
  }

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Stable load function - only changes when mine changes
  const isMine = !!mine;
  const load = useCallback((params: Record<string, unknown>) => loadBots(params, isMine), [isMine]);

  // Stable error handler
  const onLoadError = useCallback((e: unknown) => handleError(getErrorMessage(e, "Bots loading failed")), []);

  const {
    items: bots,
    setItems: setBots,
    refresh,
    isLoading,
    ...other
  } = useStrapiList<BotConfig>({
    ...otherProps,
    filters: rawFilters,
    transformFilters,
    load,
    onLoadError,
  });

  // Update cache when fresh data arrives (only if changed)
  useEffect(() => {
    if (!mountedRef.current) return;
    if (!isLoading && bots.length > 0 && cacheKey) {
      // Only update if data actually changed
      if (hasDataChanged(cachedItems, bots)) {
        setToCache(cacheKey, bots);
        setCachedItems(bots);
      }
      setIsRefreshing(false);
    }
  }, [bots, isLoading, cacheKey, cachedItems]);

  // Track refreshing state
  useEffect(() => {
    if (!mountedRef.current) return;
    if (isLoading && hasInitialCache.current) {
      setIsRefreshing(true);
    }
  }, [isLoading]);

  // Use cached data if available while loading
  const items = cacheKey && cachedItems.length > 0 ? cachedItems : bots;
  const effectiveLoading = cacheKey ? !hasInitialCache.current && isLoading : isLoading;

  return {
    bots: items,
    setBots,
    refresh,
    isLoading: effectiveLoading,
    isRefreshing,
    ...other,
    filters: other.filters,
  };
};

export const loadBots = async (params: Record<string, unknown>, mine: boolean) => {
  try {
    if (mine) return botConfigsEndpoints.getMyBotConfigs(params);
    else return botConfigsEndpoints.getBotConfigs(params);
  } catch (e: unknown) {
    handleError(getErrorMessage(e, "Bots loading failed"));
    return undefined;
  }
};

export default useBots;
